/* emoji-search — multilingual indexed search.
 *
 * Index strategy: build a single string per emoji combining its localised name,
 * tags, hex, and shortcodes, then match queries via includes. For ~1 940 emojis
 * a flat scan is fast enough (<3 ms on a mid-range phone), so we skip a trie.
 *
 * Special query forms:
 *   - emoji char in input  → exact char lookup (also walks ZWJ-normalised forms)
 *   - hex (1F600 / U+1F600 / U1F600 / 0x1F600) → direct lookup
 *   - :shortcode:          → shortcode index
 *
 * RANKED SEARCH BY WORD STEM (05.11.2026, /mnemonic-table).
 *   A substring scan cannot find «берёзы», «котята», «Vögel», «눈이» — the
 *   catalogue holds «береза», «кошка», «Vogel», «눈». rank() matches WORDS:
 *   each word of the text is normalised and stemmed with the SITE SEARCH
 *   stemmer (window.TimbricaSearch._internal — the same normalize()/stem() the
 *   header search runs), and compared with the stems of every emoji's label and
 *   tags. A word that matches the WHOLE one-word label («кот» → cat) outweighs a
 *   word of a longer label, which outweighs a tag. Ranked, not filtered: the
 *   caller gets weights and decides.
 *
 *   rank(text, opts)        → [{hex, score, hits}]  best first
 *   rankWords(words, opts)  → [{word, hits:[{hex, w}]}]  per word (the caller
 *                              combines words itself — /mnemonic-table does)
 *   words(text)             → ['word', …]  the tokenizer rank() uses
 *   keyOf(word, opts)       → the index key of a word (a caller's own dictionary
 *                              must be stemmed exactly like the catalogue)
 *   opts.variants(word)     → ['form', …]  extra spellings to try for a TEXT
 *                              word (Korean particles, diminutives). Index keys
 *                              stay plain; only the text side is expanded, so a
 *                              rule can never make two catalogue words collide.
 *   opts.stemmer            → {normalize(s), stem(s)} — defaults to the site
 *                              search's; without it, lowercase + ё→е.
 *   opts.limit              → default 60
 *
 *   query() keeps its substring contract; when the substring scan finds
 *   nothing it now answers with rank() — «берёзы» on /emoji used to say
 *   «nothing found». ё/е are folded on both sides of the substring scan.
 */
(function(){
    'use strict';
    const ns = window.Emoji = window.Emoji || {};
    ns.search = ns.search || {};

    const foldYo = s => String(s || '').toLowerCase().replace(/ё/g, 'е');

    let index = null; // [{hex, blob}]
    function buildIndex(){
        if (index) return index;
        const out = [];
        for (const em of ns.allEmojis()){
            const label = foldYo(ns.label(em.h));
            const tags  = foldYo(ns.tags(em.h).join(' '));
            const codes = ns.shortcodes(em.h).join(' ').toLowerCase();
            out.push({ hex: em.h, blob: `${em.h.toLowerCase()} ${label} ${tags} ${codes}` });
        }
        index = out;
        return out;
    }
    ns.search.invalidate = function(){ index = null; stemIndex = null; };

    const HEX_RE = /^(?:U\+?|0x|\\u\{?)?([0-9a-f]{4,6})(?:[\s-]+([0-9a-f]{4,6}))*\}?$/i;
    const SHORTCODE_RE = /^:([\w_+-]+):?$/;

    ns.search.query = function(q){
        q = (q || '').trim();
        if (!q) return ns.allEmojis().map(e => e.h);

        // 1) Pasted emoji character — find by char (try plain, FE0F-stripped, FE0F-added)
        if (containsEmojiChar(q)){
            const exact = ns.byChar(q);
            if (exact) return [exact];
            const stripped = q.replace(/️/g, '');
            const sExact = ns.byChar(stripped);
            if (sExact) return [sExact];
            // ZWJ-tokenised — look up first segment
            const first = Array.from(q.split('‍')[0])[0];
            const fExact = first && ns.byChar(first);
            if (fExact) return [fExact];
        }

        // 2) Shortcode
        const sm = q.match(SHORTCODE_RE);
        if (sm){
            const hex = ns.byShortcode(sm[1]);
            return hex ? [hex] : [];
        }

        // 3) Hex
        const hm = q.match(HEX_RE);
        if (hm){
            const hex = q.replace(/^(U\+?|0x|\\u\{?)/i, '').replace(/\}?$/, '').replace(/\s+/g, '-').toUpperCase();
            if (ns.has(hex)) return [hex];
        }

        // 4) Substring scan over indexed text
        const ql = foldYo(q);
        buildIndex();
        const out = [];
        for (const it of index){
            if (it.blob.indexOf(ql) !== -1) out.push(it.hex);
        }
        if (out.length) return out;
        // 5) Nothing as a substring — the words may be inflected («берёзы», «котята»).
        return ns.search.rank(q).map(r => r.hex);
    };

    /* ── ranked search by word stem ─────────────────────────────────────── */

    // Letters and digits, with an apostrophe kept INSIDE a word: Ukrainian
    // spells «м'яч», «п'ять» with it. A French elision («l'arbre», «d'eau») is
    // split off below — before the apostrophe stands one or two Latin letters.
    const WORD_RE = /[\p{L}\p{M}\p{N}]+(?:['’ʼ][\p{L}\p{M}\p{N}]+)*/gu;
    const ELISION_RE = /^[a-z]{1,2}['’]/i;
    ns.search.words = function(text){
        const out = [];
        const m = String(text || '').match(WORD_RE) || [];
        for (let w of m){
            if (ELISION_RE.test(w)) w = w.replace(ELISION_RE, '');
            if (w) out.push(w);
        }
        return out;
    };

    function siteStemmer(){
        const se = window.TimbricaSearch && window.TimbricaSearch._internal;
        if (se && typeof se.stem === 'function' && typeof se.normalize === 'function') return se;
        return null;
    }
    function keyFn(stemmer){
        const se = stemmer || siteStemmer();
        if (!se) return w => foldYo(w).trim();
        return w => {
            const n = se.normalize(w);
            return n ? se.stem(n) : '';
        };
    }

    // Weights. A one-word label equal to the word is the catalogue saying «this
    // emoji IS that thing» (кот, дом, гриб); a word inside a longer label is
    // weaker («кленовый лист» for «лист»), a tag weaker still («листва» on 🌳).
    // Two adjacent words matching two adjacent words of a label or a tag (a
    // pair) are the strongest evidence of all — «божья коровка», «maple leaf».
    const W = { labelWhole: 6, pair: 7, labelWord: 4, tagWhole: 3, tagWord: 2 };

    let stemIndex = null; // {sig, map: Map(key → Map(hex → w))}
    function addKey(map, key, hex, w){
        if (!key) return;
        let m = map.get(key);
        if (!m) map.set(key, m = new Map());
        if (!(m.get(hex) >= w)) m.set(hex, w);
    }
    function indexPhrase(map, key, phrase, hex, whole, word){
        const ws = ns.search.words(phrase).map(key).filter(Boolean);
        if (!ws.length) return;
        if (ws.length === 1){ addKey(map, ws[0], hex, whole); return; }
        for (let i = 0; i < ws.length; i++){
            addKey(map, ws[i], hex, word);
            if (i) addKey(map, ws[i - 1] + ' ' + ws[i], hex, W.pair);
        }
    }
    function buildStemIndex(stemmer){
        const sig = stemmer || siteStemmer() || 'plain';
        if (stemIndex && stemIndex.sig === sig) return stemIndex.map;
        const key = keyFn(stemmer);
        const map = new Map();
        for (const em of ns.allEmojis()){
            indexPhrase(map, key, ns.label(em.h), em.h, W.labelWhole, W.labelWord);
            for (const t of ns.tags(em.h)) indexPhrase(map, key, t, em.h, W.tagWhole, W.tagWord);
        }
        stemIndex = { sig, map };
        return map;
    }

    // Per word: [{hex, w}] best first. A word's later variants count a little
    // less than the word itself, so the plain reading wins a tie.
    ns.search.rankWords = function(words, opts){
        opts = opts || {};
        const map = buildStemIndex(opts.stemmer);
        const key = keyFn(opts.stemmer);
        const variants = typeof opts.variants === 'function' ? opts.variants : null;
        const keys = words.map(w => {
            const forms = [w].concat(variants ? (variants(w) || []) : []);
            const seen = new Set(), out = [];
            for (const f of forms){ const k = key(f); if (k && !seen.has(k)){ seen.add(k); out.push(k); } }
            return out;
        });
        return words.map((word, i) => {
            const best = new Map();
            const take = (k, factor) => {
                const m = map.get(k);
                if (!m) return;
                m.forEach((w, hex) => { const v = w * factor; if (!(best.get(hex) >= v)) best.set(hex, v); });
            };
            keys[i].forEach((k, j) => take(k, j ? 0.9 : 1));
            // A pair of this word and the next one.
            if (i + 1 < words.length) keys[i].forEach(a => keys[i + 1].forEach(b => take(a + ' ' + b, 1)));
            const hits = [];
            best.forEach((w, hex) => hits.push({ hex, w }));
            hits.sort((a, b) => b.w - a.w || order(a.hex) - order(b.hex));
            return { word, keys: keys[i], hits };
        });
    };

    function order(hex){ const em = ns.get && ns.get(hex); return em && em.o != null ? em.o : 1e9; }

    // The index key of one word — for a caller that keeps its OWN word list next
    // to the catalogue (a tool's dictionary) and must stem it exactly like this.
    ns.search.keyOf = function(word, opts){ return keyFn(opts && opts.stemmer)(word); };

    ns.search.rank = function(text, opts){
        opts = opts || {};
        const words = ns.search.words(text);
        if (!words.length) return [];
        const per = ns.search.rankWords(words, opts);
        const acc = new Map();
        per.forEach(p => p.hits.forEach(h => {
            const a = acc.get(h.hex) || { hex: h.hex, score: 0, hits: 0 };
            a.score += h.w; a.hits++;
            acc.set(h.hex, a);
        }));
        const out = Array.from(acc.values());
        out.sort((a, b) => b.score - a.score || b.hits - a.hits || order(a.hex) - order(b.hex));
        return out.slice(0, opts.limit || 60);
    };

    function containsEmojiChar(s){
        // Quick test: any code-point above 0x1F00 OR a regional indicator pair
        for (const ch of s){
            const cp = ch.codePointAt(0);
            if (cp >= 0x1F000 || cp === 0x200D || cp === 0xFE0F || (cp >= 0x2600 && cp <= 0x27BF)) return true;
        }
        return false;
    }
})();
